import { AlertTriangle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useQuota } from "@/hooks/useQuota";
import { cn } from "@/lib/utils";

/**
 * Global storage-usage watcher: a one-per-session info toast at ~50%, and a
 * persistent banner at ~75% (warning) / ~90% (critical). The critical *modal*
 * on a write is handled at the write site (see the upload flow's QuotaModal).
 */
export function QuotaWatcher(): JSX.Element | null {
  const navigate = useNavigate();
  const { estimate, warningLevel } = useQuota({ autoRefresh: true, intervalMs: 60_000 });
  const infoShown = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (warningLevel === "info" && !infoShown.current) {
      infoShown.current = true;
      toast("Local storage is about half full.", {
        description:
          "CatalogKit keeps snapshots in your browser. Manage them anytime under Profiles.",
      });
    }
  }, [warningLevel]);

  // A dismissed banner returns if usage drops below the threshold and rises again.
  useEffect(() => {
    if (warningLevel === "ok" || warningLevel === "info") setDismissed(false);
  }, [warningLevel]);

  if ((warningLevel !== "warning" && warningLevel !== "critical") || dismissed) return null;

  const pct = estimate ? Math.round(estimate.percent * 100) : 0;
  const critical = warningLevel === "critical";

  return (
    <div
      role="status"
      className={cn(
        "mb-4 flex items-center gap-3 rounded-md border px-4 py-2 text-sm",
        critical
          ? "border-destructive/50 bg-destructive/10 text-destructive"
          : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
      )}
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="flex-1">
        {critical
          ? `Local storage is ${pct}% full — new snapshots may fail to save.`
          : `Local storage is ${pct}% full.`}
      </span>
      <Button
        size="sm"
        variant={critical ? "destructive" : "outline"}
        onClick={() => navigate("/profiles")}
      >
        Manage storage
      </Button>
      {!critical ? (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss storage warning"
          className="opacity-70 hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
